import { z } from 'zod';
import { billingReviewStatusSchema } from './common.js';
export const billingDecisionSchema = z.object({
    status: billingReviewStatusSchema.exclude(['pending', 'invoiced']),
    reason: z.string().max(2_000).optional(),
});
export const projectDepositInputSchema = z.object({
    originalAmountMinor: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
    currency: z.enum(['CAD', 'USD']),
});
export const depositApplicationInputSchema = z.object({
    amountMinor: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    reason: z.string().trim().min(1).max(500),
    idempotencyKey: z.uuid(),
});
export const projectDepositSummarySchema = z.object({
    id: z.uuid(),
    projectId: z.uuid(),
    projectName: z.string(),
    clientName: z.string(),
    currency: z.enum(['CAD', 'USD']),
    originalAmountMinor: z.number().int().nonnegative(),
    appliedAmountMinor: z.number().int().nonnegative(),
    remainingAmountMinor: z.number().int().nonnegative(),
    updatedAt: z.iso.datetime(),
});
export const billingBlockerSchema = z.enum([
    'missing_billable_minutes',
    'missing_role',
    'missing_epic',
    'missing_bill_rate',
    'open_validation_issue',
    'unallocatable_consolidated_time',
    'configured_consolidated_epic_missing',
    'review_incomplete',
]);
export const billingPeriodQuerySchema = z
    .object({
    projectId: z.uuid(),
    from: z.iso.date(),
    to: z.iso.date(),
})
    .refine((value) => value.from <= value.to, { message: 'from must not follow to' });
export const billingPreviewLineSchema = z.object({
    groupKey: z.string().min(1),
    role: z.string().nullable(),
    epicKey: z.string().nullable(),
    epicSummary: z.string().nullable(),
    sourceBillableMinutes: z.number().int().nonnegative(),
    allocatedConsolidatedMinutes: z.number().int().nonnegative(),
    finalBillableMinutes: z.number().int().nonnegative(),
    rateAmountMinor: z.number().int().positive().nullable(),
    amountMinor: z.number().int().nonnegative().nullable(),
    blockers: z.array(billingBlockerSchema),
    timeEntryIds: z.array(z.uuid()),
});
export const billingPeriodPreviewSchema = z.object({
    projectId: z.uuid(),
    periodStart: z.iso.date(),
    periodEnd: z.iso.date(),
    currency: z.enum(['CAD', 'USD']),
    loggedMinutes: z.number().int().nonnegative(),
    billableMinutes: z.number().int().nonnegative(),
    normalBillableMinutes: z.number().int().nonnegative(),
    consolidatedBillableMinutes: z.number().int().nonnegative(),
    finalBillableMinutes: z.number().int().nonnegative(),
    totalAmountMinor: z.number().int().nonnegative().nullable(),
    blockers: z.array(billingBlockerSchema),
    lines: z.array(billingPreviewLineSchema),
});
export const billingValidationIssueStatusSchema = z.enum(['open', 'resolved', 'ignored']);
export const billingValidationIssueKindSchema = z.enum([
    'worklog_on_epic',
    'worklog_on_subtask',
    'unknown_issue_hierarchy',
    'missing_epic',
    'jira_tempo_account_mismatch',
    'suspected_epic_account_mismatch',
]);
export const billingValidationDecisionSchema = z.object({
    status: z.enum(['open', 'ignored']),
});
export const billingValidationGroupSchema = z.object({
    groupKey: z.string().min(1),
    projectId: z.uuid(),
    projectName: z.string().min(1),
    kind: billingValidationIssueKindSchema,
    priority: z.enum(['medium', 'high', 'critical']),
    status: billingValidationIssueStatusSchema,
    jiraIssueId: z.string().nullable(),
    jiraIssueKey: z.string().nullable(),
    title: z.string().nullable(),
    workDateFrom: z.iso.date(),
    workDateTo: z.iso.date(),
    tempoAccountKey: z.string().nullable(),
    affectedWorklogCount: z.number().int().positive(),
    issueIds: z.array(z.uuid()).min(1),
    evidence: z.record(z.string(), z.unknown()),
    sourceUrl: z.url().nullable(),
});
export const billingValidationGroupDecisionSchema = z.object({
    issueIds: z.array(z.uuid()).min(1).max(500),
    status: z.enum(['open', 'ignored']),
});
export const billingConsolidatedEpicCandidateSchema = z.object({
    workItemId: z.uuid(),
    jiraIssueId: z.string().min(1),
    jiraIssueKey: z.string().min(1),
    title: z.string().min(1),
});
export const projectBillingSettingsSchema = z.object({
    projectId: z.uuid(),
    consolidatedEpicJiraIssueId: z.string().nullable(),
    candidates: z.array(billingConsolidatedEpicCandidateSchema),
});
export const projectBillingSettingsInputSchema = z.object({
    consolidatedEpicJiraIssueId: z.string().trim().min(1).max(128).nullable(),
});
export const createPeriodBillingBatchSchema = z
    .object({
    projectId: z.uuid(),
    periodStart: z.iso.date(),
    periodEnd: z.iso.date(),
    idempotencyKey: z.string().min(16).max(128),
})
    .refine((value) => value.periodStart <= value.periodEnd, {
    message: 'periodStart must not follow periodEnd',
});
export const blockedWorklogsSummaryResponseSchema = z.object({
    data: z.object({ total: z.number().int().nonnegative() }),
});
//# sourceMappingURL=billing.js.map